import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { PageSEO } from './components/PageSEO';
import { OrganizationSchema, WebSiteSchema, InvestmentFundSchema } from './components/StructuredData';
import { Home } from './pages/Home';
import { Approach } from './pages/Approach';
import { Portfolio } from './pages/Portfolio';
import { Founders } from './pages/Founders';
import { Team } from './pages/Team';
import { Values } from './pages/Values';
import { Updates } from './pages/Updates';
import { Contact } from './pages/Contact';
const seoConfig: Record<string, {
  title: string;
  description: string;
  keywords: string;
  ogType?: string;
}> = {
  '/': {
    title: 'Vitality Venture Fund | Biomimicry-Driven Venture Capital',
    description: "Vitality is a venture fund investing in deep tech and hard tech startups rooted in biomimicry. We fuel nature-inspired innovation by applying 3.8 billion years of nature's R&D combined with modern AI and technology across agriculture, construction, and materials.",
    keywords: 'biomimicry venture capital, nature-inspired investing, deep tech fund, hard tech startups, regenerative agriculture investment, climate tech VC, bio-based materials, circular economy fund'
  },
  '/approach': {
    title: 'Our Approach — Biomimicry Investment Thesis | Vitality Venture Fund',
    description: "Vitality uses a biomimicry-based investment filter to identify pre-seed and seed stage startups creating essential solutions. We invest across three themes — Grow (agriculture), Build (construction), and Use (materials) — applying nature's 3.8 billion years of R&D.",
    keywords: 'biomimicry investment thesis, nature-inspired startups, pre-seed venture fund, seed stage investing, regenerative agriculture, bio-based construction, circular materials, deep tech investment strategy'
  },
  '/portfolio': {
    title: 'Portfolio — Nature-Inspired Startups | Vitality Venture Fund',
    description: "Explore Vitality's portfolio of biomimicry-driven startups: BOPA (autonomous nano-tractors for regenerative agriculture), TierraSphere (AI for soil regeneration), ReefCycle (nature-based cement), and Mothership Materials (containerized biomanufacturing).",
    keywords: 'BOPA precision ag, TierraSphere soil AI, ReefCycle cement, Mothership Materials biomanufacturing, biomimicry startups, nature-based solutions portfolio, regenerative agriculture startups'
  },
  '/founders': {
    title: 'Natalya Wallin — Founder & GP | Vitality Venture Fund',
    description: 'Meet Natalya Wallin, GP & Managing Director of Vitality Venture Fund. A high-conviction leader with 10+ years deploying a $75M global fund, backgrounds in biology and molecular genetics, Executive MBA from Duke, and Moab 240-mile ultramarathon finisher.',
    keywords: 'Natalya Wallin, Vitality founder, venture capital GP, biomimicry investor, ultramarathon entrepreneur, Duke MBA, molecular genetics, women in venture capital',
    ogType: 'profile'
  },
  '/team': {
    title: 'Team — Multidisciplinary Experts in Nature-Inspired Innovation | Vitality',
    description: 'Meet the Vitality team: Natalya Wallin (GP), Patrick Livingston (Built Environment), Rollin Nash (Operations), plus advisors and an Investment Review Committee with expertise spanning engineering, biology, climate finance, and fund management.',
    keywords: 'Vitality team, venture capital team, biomimicry experts, built environment investing, operations specialist, investment review committee, climate finance advisors'
  },
  '/values': {
    title: 'Values — Conviction, Athleticism, Adventure, Connection | Vitality',
    description: "Vitality's four core values: Conviction to fuel nature-inspired startups, Athleticism to navigate unknowns with grit and discipline, Adventure to explore bold ventures with curiosity, and Connection across disciplines, sectors, and industries.",
    keywords: 'venture capital values, conviction investing, nature-inspired innovation values, biomimicry fund culture, interdisciplinary collaboration'
  },
  '/updates': {
    title: 'Updates — News, Press & Events | Vitality Venture Fund',
    description: 'Latest from Vitality: featured in USA Today, International Business Times, and CEO Weekly. Events including Vital Systems NYC, partnerships with ASU Biomimicry Center and Emergent Campus, and investment announcements.',
    keywords: 'Vitality news, biomimicry venture capital press, USA Today Vitality, nature-inspired investing events, ASU Biomimicry Center partnership, Vital Systems event'
  },
  '/contact': {
    title: 'Contact Vitality Venture Fund — Founders & Investors Welcome',
    description: "Connect with Vitality Venture Fund. Whether you're a founder building nature-inspired solutions, an investor aligned with biomimicry-driven innovation, or a partner in regenerative technology, we'd love to hear from you.",
    keywords: 'contact Vitality, biomimicry venture capital contact, nature-inspired startup funding, venture fund inquiry, Natalya Wallin contact'
  }
};
// SEO manager that updates meta tags on route change
function SEOManager() {
  const {
    pathname
  } = useLocation();
  const seo = seoConfig[pathname] || seoConfig['/'];
  return <PageSEO title={seo.title} description={seo.description} path={pathname} keywords={seo.keywords} ogType={seo.ogType} data-id="element-0" />;
}
// Scroll to top on route change
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
export function App() {
  return <Router data-id="element-1">
      <div className="flex flex-col min-h-screen bg-deepCanopy text-white font-sans selection:bg-vitalityTeal selection:text-deepCanopy" data-id="element-2">
        <ScrollToTop data-id="element-3" />
        <SEOManager data-id="element-4" />
        <OrganizationSchema data-id="element-5" />
        <WebSiteSchema data-id="element-6" />
        <InvestmentFundSchema data-id="element-7" />
        <Navigation data-id="element-8" />
        <main className="flex-grow" data-id="element-9">
          <Routes data-id="element-10">
            <Route path="/" element={<Home data-id="element-12" />} data-id="element-11" />
            <Route path="/approach" element={<Approach data-id="element-14" />} data-id="element-13" />
            <Route path="/portfolio" element={<Portfolio data-id="element-16" />} data-id="element-15" />
            <Route path="/founders" element={<Founders data-id="element-18" />} data-id="element-17" />
            <Route path="/team" element={<Team data-id="element-20" />} data-id="element-19" />
            <Route path="/values" element={<Values data-id="element-22" />} data-id="element-21" />
            <Route path="/updates" element={<Updates data-id="element-24" />} data-id="element-23" />
            <Route path="/contact" element={<Contact data-id="element-26" />} data-id="element-25" />
          </Routes>
        </main>
        <Footer data-id="element-27" />
      </div>
    </Router>;
}